import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedRoute } from "@/lib/routes";
import { SITE_CONFIG } from "@/config/site";
import { scrollToTop } from "@/components/common/SmoothScroll";
import LoopVideo from "@/components/motion/LoopVideo";
import PillButton from "@/components/ui/PillButton";
import Lines from "@/components/motion/Lines";
import Scramble from "@/components/motion/Scramble";
import { SERVICE_CATALOG, serviceVideo } from "@/data/serviceCatalog";

/**
 * Every page ends here: the closing call to action over the fibre clip (left
 * out on the contact page, which is already that), the link columns, and the
 * wordmark set full width.
 */
const socials = [
  { label: "LinkedIn", href: "https://linkedin.com/company/byteet" },
  { label: "Instagram", href: "https://instagram.com/byteetnl" },
  { label: "Facebook", href: "https://facebook.com/byteet" },
];

const EnhancedFooter = ({ cta = true }: { cta?: boolean }) => {
  const { t, language } = useLanguage();
  const year = new Date().getFullYear();
  const fiber = serviceVideo("fiber");
  const { address } = SITE_CONFIG;
  // Only the parts the market has: byteet.md lists the city alone.
  const addressLine = [address.streetAddress, [address.postalCode, address.locality].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  const pages = [
    { name: t("nav.services"), href: SITE_CONFIG.routes.services },
    { name: t("nav.portfolio"), href: SITE_CONFIG.routes.portfolio },
    { name: t("nav.articles"), href: SITE_CONFIG.routes.articles },
    { name: t("nav.about"), href: SITE_CONFIG.routes.about },
    { name: t("nav.contact"), href: SITE_CONFIG.routes.contact },
  ];

  return (
    <footer className="uncontained bg-ink text-white">
      {cta && (
        <div className="relative overflow-hidden px-5 pb-20 pt-28 md:px-8 md:pb-32 md:pt-40">
          <LoopVideo src={fiber.src} poster={fiber.poster} className="absolute inset-0 opacity-45" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-ink via-ink/40 to-ink" />
          <div className="relative">
            <p className="label-mono mb-6 text-white/55">
              <Scramble text={t("home.ctaLabel")} />
            </p>
            <Lines
              as="p"
              lines={[t("home.ctaLead"), t("home.ctaTail")]}
              lineClassName={(i) => (i === 1 ? "text-outline" : "")}
              className="display text-[clamp(3.5rem,12vw,13rem)]"
            />
            <div className="mt-10 grid gap-8 md:mt-14 md:grid-cols-12 md:items-end">
              <p className="max-w-lg text-lg text-white/70 md:col-span-5">{t("hero.cta.title")}</p>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 md:col-span-7 md:justify-end">
                <PillButton tone="light" to={getLocalizedRoute(SITE_CONFIG.routes.book, language)}>
                  {t("hero.cta.consultation")}
                </PillButton>
                <a href={`mailto:${SITE_CONFIG.email}`} className="label-mono text-white/75 hover:text-white">
                  {SITE_CONFIG.email}
                </a>
                <a href={`tel:${SITE_CONFIG.phone}`} className="label-mono text-white/75 hover:text-white">
                  {SITE_CONFIG.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-12 border-t border-white/15 px-5 py-16 md:grid-cols-12 md:px-8">
        <nav className="md:col-span-5" aria-label={t("footer.services")}>
          <p className="label-mono mb-5 text-white/45">{t("footer.services")}</p>
          <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {SERVICE_CATALOG.map((s) => (
              <li key={s.id}>
                <Link to={getLocalizedRoute(`${SITE_CONFIG.routes.services}/${s.id}`, language)} className="text-white/75 transition-colors hover:text-white">
                  {t(`serviceOptions.${s.key}`)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav className="md:col-span-3" aria-label={t("footer.pages")}>
          <p className="label-mono mb-5 text-white/45">{t("footer.pages")}</p>
          <ul className="space-y-2">
            {pages.map((p) => (
              <li key={p.href}>
                <Link to={getLocalizedRoute(p.href, language)} className="text-white/75 transition-colors hover:text-white">
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="md:col-span-4">
          <p className="label-mono mb-5 text-white/45">{t("home.ctaLabel")}</p>
          <address className="space-y-2 not-italic text-white/75">
            <a href={`mailto:${SITE_CONFIG.email}`} className="block hover:text-white">{SITE_CONFIG.email}</a>
            <a href={`tel:${SITE_CONFIG.phone}`} className="block hover:text-white">{SITE_CONFIG.phoneDisplay}</a>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${SITE_CONFIG.name}, ${addressLine}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:text-white"
            >
              {addressLine}
            </a>
          </address>
          <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
            {socials.map((s) => (
              <li key={s.label}>
                <a href={s.href} target="_blank" rel="noopener noreferrer" className="label-mono text-white/60 hover:text-white">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* The wordmark, sized so the six letters span the page. */}
      <div aria-hidden="true" className="overflow-hidden px-3 md:px-5">
        <p className="display select-none whitespace-nowrap text-center text-[23.5vw] leading-[0.78] tracking-[-0.06em] text-white">
          BYTEET
        </p>
      </div>

      <div className="label-mono flex flex-col gap-4 border-t border-white/15 px-5 py-6 text-[0.68rem] text-white/45 md:flex-row md:items-center md:justify-between md:px-8">
        <p>
          © {year} {SITE_CONFIG.name}. {t("footer.copyright")} · {SITE_CONFIG.legal.entity} · IDNO {SITE_CONFIG.legal.idno}
        </p>
        <div className="flex flex-wrap items-center gap-5">
          <Link to={getLocalizedRoute(SITE_CONFIG.routes.privacy, language)} className="hover:text-white">{t("footer.privacyPolicy")}</Link>
          <Link to={getLocalizedRoute(SITE_CONFIG.routes.terms, language)} className="hover:text-white">{t("footer.termsOfService")}</Link>
          <button type="button" onClick={scrollToTop} className="inline-flex items-center gap-1.5 hover:text-white">
            <ArrowUp className="h-3 w-3" /> {t("footer.backToTop")}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default EnhancedFooter;
